import React from 'react';
import { Check, ChevronRight, ChevronsRight, Info, PenLine, Star } from 'lucide-react';
import type { KatalogSprache } from '../../services/mangelKatalog/typen';
import { KatalogPikto } from './KatalogPikto';
import { LANGDRUCK_KLASSEN, useLangdruck } from './langdruck';
import { katalogTexte } from './texte';

/**
 * Kachel der Ebene 3 („Standardmangel per Torte“, Paket D): Knopf mit
 * Piktogramm und Kurztext (höchstens 3 Zeilen). Ein Mangel hat darunter zwei
 * eigene Knöpfe: ⓘ (Überschrift, Beschreibung, Vorschrift) und ☆/★ (Favorit), je ≥ 44 px.
 *
 * Arten:
 * - 'mangel'  → wählt aus
 * - 'gruppe'  → öffnet (nur bei tieferen Bäumen)
 * - 'weiter'  → „Sonstiges (+n)“, blättert
 * - 'freitext'→ „Freitext in <Kategorie>“
 * - 'leer'    → blasser Platzhalter, nicht fokussier- und nicht klickbar
 *
 * `platz` (0 … 8) landet als data-platz am Hauptknopf (Pfeiltasten im Raster).
 *
 * Mehrfachauswahl (Paket L, 30.09.): mit `onHalten` löst Gedrückthalten
 * (Rechtsklick, Strg-/Umschalt-Klick) onHalten aus, ein kurzer Tipp onWahl.
 * `gewaehlt` (nur im Sammelmodus gesetzt) zeigt Haken und aria-pressed.
 */
export interface KatalogKachelProps {
    art: 'mangel' | 'gruppe' | 'weiter' | 'freitext' | 'leer';
    platz: number;
    /** Beschriftung (Kurztext, „Freitext in Absturz“ …). */
    kurz?: string;
    pikto?: string;
    /** 'weiter': Zahl der folgenden Einträge. */
    rest?: number;
    favorit?: boolean;
    onWahl?: () => void;
    onInfo?: () => void;
    onFavorit?: () => void;
    /** 'mangel': Gedrückthalten → Mehrfachauswahl. */
    onHalten?: () => void;
    /** 'mangel', nur im Sammelmodus: im Korb (Haken, aria-pressed). */
    gewaehlt?: boolean;
    /** 'freitext': im Sammelmodus gesperrt. */
    gesperrt?: boolean;
    /** Feste Beschriftungen („Sonstiges“/„More“, aria); Standard deutsch (28.09.). */
    sprache?: KatalogSprache;
}

const HOEHE = 'min-h-[8.5rem]';
const nichts = () => undefined;

const KNOPF_BASIS =
    'flex w-full flex-1 flex-col items-center justify-center gap-1.5 px-1.5 py-2 text-center motion-safe:transition-colors';

export const KatalogKachel: React.FC<KatalogKachelProps> = ({
    art,
    platz,
    kurz = '',
    pikto,
    rest,
    favorit = false,
    onWahl,
    onInfo,
    onFavorit,
    onHalten,
    gewaehlt,
    gesperrt = false,
    sprache = 'de',
}) => {
    const t = katalogTexte(sprache);
    // Vor den frühen Returns (Hook-Regeln); wirkt nur an der Mangel-Kachel mit onHalten.
    const druck = useLangdruck({ onHalten: onHalten ?? nichts, onKurz: onWahl ?? nichts });
    if (art === 'leer') {
        return <div aria-hidden="true" className={`${HOEHE} rounded-xl border border-stone-200 bg-stone-100/60 opacity-60`} />;
    }

    if (art === 'weiter') {
        const zusatz = typeof rest === 'number' && rest > 0 ? ` (+${rest})` : '';
        return (
            <button
                type="button"
                data-platz={platz}
                onClick={onWahl}
                aria-label={`${t.sonstiges}${rest ? `, ${t.weitere(rest)}` : ''}`}
                className={`${HOEHE} ${KNOPF_BASIS} rounded-xl border border-dashed border-stone-400 bg-stone-100 text-stone-700 hover:bg-stone-200 active:bg-stone-200`}
            >
                <ChevronsRight size={28} aria-hidden="true" className="text-stone-500" />
                <span className="text-[13px] font-semibold leading-tight">{t.sonstiges}{zusatz}</span>
            </button>
        );
    }

    if (art === 'freitext') {
        return (
            <button
                type="button"
                data-platz={platz}
                onClick={onWahl}
                disabled={gesperrt}
                aria-label={kurz}
                className={`${HOEHE} ${KNOPF_BASIS} rounded-xl border border-dashed border-stone-400 bg-white text-stone-700 hover:bg-stone-50 active:bg-stone-200 disabled:pointer-events-none disabled:opacity-50`}
            >
                <PenLine size={26} aria-hidden="true" className="text-stone-500" />
                <span className="line-clamp-3 text-[12px] font-semibold leading-tight">{kurz}</span>
            </button>
        );
    }

    if (art === 'gruppe') {
        return (
            <button
                type="button"
                data-platz={platz}
                onClick={onWahl}
                aria-label={kurz}
                className={`${HOEHE} ${KNOPF_BASIS} rounded-xl border border-stone-300 bg-white text-stone-800 hover:bg-stone-50 active:bg-stone-200`}
            >
                <KatalogPikto schluessel={pikto} groesse={36} />
                <span className="line-clamp-3 text-[12px] font-semibold leading-tight">{kurz}</span>
                <ChevronRight size={16} aria-hidden="true" className="text-stone-400" />
            </button>
        );
    }

    const mitLeiste = !!onInfo || !!onFavorit;
    const sammeln = gewaehlt !== undefined;
    return (
        <div
            className={`${HOEHE} relative flex flex-col overflow-hidden rounded-xl border ${
                gewaehlt ? 'border-stone-800 bg-stone-100' : 'border-stone-300 bg-white'
            }`}
        >
            <button
                type="button"
                data-platz={platz}
                {...(onHalten ? druck : { onClick: onWahl })}
                aria-label={kurz}
                aria-pressed={sammeln ? gewaehlt : undefined}
                className={`${KNOPF_BASIS} ${onHalten ? LANGDRUCK_KLASSEN : ''} text-stone-800 ${
                    gewaehlt ? 'hover:bg-stone-200 active:bg-stone-300' : 'hover:bg-stone-50 active:bg-stone-200'
                }`}
            >
                <KatalogPikto schluessel={pikto} groesse={36} />
                <span className="line-clamp-3 text-[12px] font-semibold leading-tight">{kurz}</span>
            </button>
            {gewaehlt && (
                <span
                    aria-hidden="true"
                    className="pointer-events-none absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-stone-800 text-white"
                >
                    <Check size={16} strokeWidth={3} />
                </span>
            )}
            {mitLeiste && (
                <div className="flex border-t border-stone-200">
                    {onInfo && (
                        <button
                            type="button"
                            aria-label={t.hinweisZu(kurz)}
                            onClick={(e) => {
                                e.stopPropagation();
                                onInfo();
                            }}
                            className="flex min-h-11 flex-1 items-center justify-center text-stone-500 hover:bg-stone-50 active:bg-stone-200"
                        >
                            <Info size={18} aria-hidden="true" />
                        </button>
                    )}
                    {onFavorit && (
                        <button
                            type="button"
                            aria-label={favorit ? t.favoritEntfernen(kurz) : t.alsFavoritMerken(kurz)}
                            aria-pressed={favorit}
                            onClick={(e) => {
                                e.stopPropagation();
                                onFavorit();
                            }}
                            className={`flex min-h-11 flex-1 items-center justify-center hover:bg-stone-50 active:bg-stone-200 ${favorit ? 'text-amber-600' : 'text-stone-500'}`}
                        >
                            <Star size={18} aria-hidden="true" fill={favorit ? 'currentColor' : 'none'} />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};
